import React, { useState, useRef, useEffect } from 'react';
import BlueButton from '../../components/BlueButton';
import { useStateContext } from '../../context/StateContext';
import { useAuthContext } from '../auth/hooks/useAuthContext';
import { AuthContext } from '../../context/AuthContext';
import T, { dT } from '../../components/Translation';
import Swal from 'sweetalert2';
import { GiStarsStack } from "react-icons/gi";
import VisDiv from './VisDiv';
import Loading, { rumiQuotes } from './Loading';

const ClaudeAiCustomDataJson = ({ 
  systemPrompt = "", 
  model= "claude-3-7-sonnet-20250219", //'claude-3-5-sonnet-20241022'
  maxNTokens = true, // Changed default to true to enable higher token limit
  temperature = 0.1,
  customData = [],
  buttonText,
  strict = false,
  aiQuestion, 
  setAiJson,
  dataExists = false,
  alertTitle,
  alertText,
  loadingCallback = () => {}
}) => {

  const { user } = useAuthContext(AuthContext)
  const { apiUrl, language, setRawAiConcert, hideTestButtons } = useStateContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadingSectionRef = useRef(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    loadingSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (!aiQuestion) {
      setError('A message is required');
      return;
    }

    setError(null);

    const messageToSend = {
      role: 'user',
      content: aiQuestion
    };
   
    const callClaude = async () => {
      setIsLoading(true);
      loadingCallback(true)
      
      try {
        // Construct system prompt with custom data
        const customDataString = customData.length === 0 ? "" : `\n\nReference Material:\n${customData.join('\n\n')}.`;
        const strictPrompt = !strict ? "" : `\n\nIMPORTANT: You must ONLY use information from the provided Reference Material above. Do not incorporate any other knowledge or information in your responses. Do not include any raw data structures or arrays in your response.`;
        const finalSystemPrompt = `${systemPrompt}${customDataString}${strictPrompt}`;

        // Send request to QStash endpoint
        const response = await fetch(`${apiUrl}/api/qstash/claude/process`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messages: [messageToSend],
            model,
            responseFormat: "json",
            stream: false, // Explicitly disable streaming to ensure complete response
            max_tokens: maxNTokens ? 8192 : 1024, // Increased to match backend setting
            temperature,
            systemPrompt: finalSystemPrompt
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to send request to QStash');
        }

        const responseData = await response.json();
        const messageId = responseData.messageId;
        
        process.env.NODE_ENV === "development" && console.log("Received message ID from QStash:", messageId);
        
        // Implement exponential backoff polling strategy with delayed start
        const maxWaitTime = 120000; // 2 minutes maximum wait time
        const initialDelay = 15000; // Wait 15 seconds before first poll
        const maxInterval = 10000; // Cap at 10 seconds per request
        
        let currentInterval = maxInterval; // After initial delay, use reasonable interval
        let totalWaitTime = 0;
        let pollTimeoutId = null;
        
        // Initialize polling variables
        
        const pollStatus = async () => {
          try {
            if (totalWaitTime >= maxWaitTime) {
              setError('Request timed out after 2 minutes. Please try again.');
              setIsLoading(false);
              loadingCallback(false)
              return;
            }

            process.env.NODE_ENV === "development" && console.log(`Polling status for messageId: ${messageId}`);
            const statusUrl = `${apiUrl}/api/qstash/claude/status/${messageId}`;
            process.env.NODE_ENV === "development" && console.log(`Status URL: ${statusUrl}`);
            const statusResponse = await fetch(statusUrl);
            const statusData = await statusResponse.json();

            if (statusData.error) {
              // If it's just "not found", keep polling with backoff
              if (statusData.error === 'Response not found or expired') {
                // Increase interval with exponential backoff
                currentInterval = Math.min(currentInterval * 1.5, maxInterval);
                totalWaitTime += currentInterval;
                pollTimeoutId = setTimeout(pollStatus, currentInterval);
                return;
              }
              // For other errors, stop polling
              setError(`Error: ${statusData.error}`);
              setIsLoading(false);
              loadingCallback(false)
              return;
            }

            if (statusData.completed) {
              // Save the raw response in the StateContext
              if (setRawAiConcert) {
                process.env.NODE_ENV === "development" && console.log("Saving raw AI response:", statusData);
                setRawAiConcert(statusData);
              }
              
              // Just set loading to false and don't try to parse anything
              setIsLoading(false);
              loadingCallback(false)
              return;
            }
            
            // If not completed, continue polling with backoff
            currentInterval = Math.min(currentInterval * 1.5, maxInterval);
            totalWaitTime += currentInterval;
            pollTimeoutId = setTimeout(pollStatus, currentInterval);
            
          } catch (error) {
            setError(`Error polling status: ${error.message}`);
            setIsLoading(false);
            loadingCallback(false)
          }
        };
        
        // Start polling after initial delay
        process.env.NODE_ENV === "development" && console.log(`Waiting ${initialDelay/1000} seconds before first status check...`);
        pollTimeoutId = setTimeout(pollStatus, initialDelay);
        
        // Cleanup polling on component unmount
        return () => {
          if (pollTimeoutId) {
            clearTimeout(pollTimeoutId);
          }
        };
      } catch (error) {
        setError(`Error: ${error.message}`);
        setIsLoading(false);
        loadingCallback(false)
      }
    };
    try {
      if(dataExists) {
        Swal.fire({
          title:  alertTitle,
          text: alertText,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: dT({ en: `YES, PROCEED`, it:`SI', PROSEGUI`}, language),
          cancelButtonText: dT({ en: `NO, CANCEL`, it:`NO, ANNULLA`}, language)
        }).then(async(result) => {
          if (result.isConfirmed) {
            await callClaude()
          }
        })
      } else {
        await callClaude()
      }

    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error calling Claude:', error);
      if (error.status === 401) {
        setError('Invalid API key. Please check your configuration.');
      } else if (error.status === 429) {
        setError('Rate limit exceeded. Please try again later.');
      } else if (error.status === 500) {
        setError('Claude API service error. Please try again later.');
      } else if (error.message === 'Invalid response from Claude API') {
        setError('Received an invalid response from Claude. Please try again.');
      } else {
        setError(`Error: ${error.message || 'An unknown error occurred'}`);
      }
    }
  };


  return (
    <div>
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <BlueButton 
            buttonStyle={{borderRadius: '8px'}} 
            margins={false} 
            fn={handleSubmit} 
            text=""
            disabled={isLoading || !aiQuestion?.trim()}
          >
            <div style={{cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center'}}>
              <GiStarsStack size={25}/>
              <div style={{ marginLeft: '10px', fontSize: '18px' }}>
                {
                  buttonText ? buttonText : <T obj={{"en": "Generate Concert", "it": "Genera Concerto"}}/> 
                }
                
              </div>
            </div>
          </BlueButton>
{/*           <div onClick={handleSubmit} style={{ border: '1px solid #343C5F', borderRadius: '8px', padding: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <GiStarsStack size={25}/>
            <div style={{ marginLeft: '10px', fontSize: '16px' }}>
              <T obj={{"en": "Generate Concert", "it": "Genera Concerto"}}/> 
            </div>
          </div> */}
        </div>
{/*         {isLoading && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-start', 
            marginTop: '10px' 
          }}>
            <div style={{ 
              padding: '12px 16px',
              borderRadius: '12px',
              backgroundColor: '#ffffff',
              color: '#666',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              <T obj={{en: "reMind AI is thinking... it may take up to 1 minute", it: "reMind IA sta pensando... può richiedere fino a 1 minuto"}}/>
            </div>
          </div>
        )} */}
       
        <VisDiv rules={[isLoading]}>
          
          <div ref={loadingSectionRef} style={{ 
              padding: '12px 16px',
              marginTop: '10px',
              marginBottom: '20px',
              borderRadius: '12px',
              backgroundColor: '#ffffff',
              color: '#666',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              <T obj={{en: "AI is creating your concert. Be aware that Ai may make mistakes, reMind is not responsible for its content", it: "IA sta creando il tuo Concerto. IA può commettere errori, reMind non è responsabile del suo contenuto"}}/>
            </div>
            <Loading progressBar={true} quotes={rumiQuotes}/>
        </VisDiv>
        {error && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '10px' 
          }}>
            <div style={{ 
              padding: '12px 16px',
              borderRadius: '12px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              {typeof error === 'string' ? error : error?.message || 'An error occurred'}
            </div>
          </div>
        )}
    </div>
  );
};

export default ClaudeAiCustomDataJson;
